import axios from 'axios';
import { getI18n } from 'react-i18next';
import Constants from '../utils/Constants';
import { createLogger, disableLoggers, enableLoggers } from '../utils/logging';
import { hideInfoNotifications, showErrorNotification, showInfoNotification, showWarningNotification } from './notifications';
import { extractAccessToken, getDaysToPasswordExpiration, isPasswordExpirationCritical, isPasswordExpirationNonnegligible, isPreAuthentication } from '../utils/authentication';
import { getSessionAccessToken, removeSessionAccessToken, setSessionAccessToken } from '../utils/browserStorage';

const logger = createLogger('Actions-Authentication');

export const preLoginUser = (userId, password) => (dispatch) => (
    axios.post(Constants.AUTH_PATH + Constants.PREAUTH_LOGIN_PATH, { userId, password })
        .then(onLoginResponse(dispatch))
        .catch(onLoginError(dispatch))
);

export const loginUser = (jwtToken, totpToken, trustDevice) => (dispatch) => (
    axios.post(Constants.AUTH_PATH + Constants.AUTH_LOGIN_PATH, { jwtToken, totpToken, trustDevice })
        .then(onLoginResponse(dispatch))
        .catch(onLoginError(dispatch))
);

export const loginSsoUser = (code, authenticationFinished) => (dispatch) => {
    const redirectUri = Constants.REDIRECT_URI.href;
    const onFulfill = (response) => {
        onLoginResponse(dispatch)(response);
        authenticationFinished();
    };
    const onRejected = (reason) => {
        onLoginError(dispatch)(reason);
        authenticationFinished();
    };
    axios.post(Constants.AUTH_PATH + Constants.AUTH_SSO_PATH, { code, redirectUri })
        .then(onFulfill)
        .catch(onRejected);
};

export const onLoginResponse = (dispatch) => (response) => {
    const jwt = extractAccessToken(response);
    if (jwt) {
        dispatch(hideInfoNotifications());

        if (isPreAuthentication(jwt)) {
            dispatch(preLoginSuccess(jwt));
        } else {
            dispatch(loginSuccess(jwt));
            dispatch(setDaysToPasswordExpiration(jwt));
            const daysToPasswordExpiration = getDaysToPasswordExpiration(jwt);
            if (isPasswordExpirationNonnegligible(daysToPasswordExpiration)) {
                dispatch(showWarningNotification(getI18n().t('login.password-expire-warning', { expireInDays: daysToPasswordExpiration })));
            } else if (isPasswordExpirationCritical(daysToPasswordExpiration)) {
                let errorNotification;
                if (daysToPasswordExpiration === 0) {
                    errorNotification = getI18n().t('login.password-expire-error-24-hours');
                } else {
                    errorNotification = getI18n().t('login.password-expire-error', { expireInDays: daysToPasswordExpiration });
                }
                dispatch(showErrorNotification(errorNotification));
            }
        }
    } else {
        dispatch(showErrorNotification(getI18n().t('login.login-failed-missing-token')));
    }
};

export const SET_DAYS_TO_PASSWORD_EXPIRATION = 'SET_DAYS_TO_PASSWORD_EXPIRATION';
export const setDaysToPasswordExpiration = (jwt) => ({
    type: SET_DAYS_TO_PASSWORD_EXPIRATION,
    payload: getDaysToPasswordExpiration(jwt),
});

export const RESET_DAYS_TO_PASSWORD_EXPIRATION = 'RESET_DAYS_TO_PASSWORD_EXPIRATION';
export const resetDaysToPasswordExpiration = () => ({
    type: RESET_DAYS_TO_PASSWORD_EXPIRATION,
});

export const USER_LOGIN_ABORT = 'USER_LOGIN_ABORT';
export const loginAbort = () => ({
    type: USER_LOGIN_ABORT,
});

export const onLoginError = (dispatch) => (error) => {
    const i18n = getI18n();
    dispatch(loginFailed());
    const { response } = error;

    if (response && response.status === 401) {
        if (response.data && response.data.error) {
            dispatch(showErrorNotification(i18n.t(`login.${response.data.error}`)));
            return;
        }
        dispatch(showErrorNotification(i18n.t('login.login-failed-unauthorized')));
        return;
    }

    let errorMessage = response ? response.statusText : error.message;
    if (!errorMessage || errorMessage.trim().length === 0) {
        if (response.status) {
            errorMessage = i18n.t('errors.http.' + response.status);
        }
    }
    dispatch(showErrorNotification(i18n.t('login.login-failed', { message: errorMessage })));
};

export const USER_PRELOGIN_SUCCESS = 'USER_PRELOGIN_SUCCESS';
export const preLoginSuccess = (jwt) => {
    return {
        type: USER_PRELOGIN_SUCCESS,
        payload: jwt,
    };
};

export const USER_LOGIN_SUCCESS = 'USER_LOGIN_SUCCESS';
export const loginSuccess = (jwt) => {
    setSessionAccessToken(jwt);
    enableLoggers();
    return {
        type: USER_LOGIN_SUCCESS,
        payload: jwt,
    };
};

export const USER_LOGIN_FAILURE = 'USER_LOGIN_FAILURE';
export const loginFailed = () => ({
    type: USER_LOGIN_FAILURE,
});

export const USER_LOGOUT = 'USER_LOGOUT';
export const logoutUser = (message, errorMessage, apolloClient) => async (dispatch) => {
    const sessionAccessToken = getSessionAccessToken();
    axios.post(
        Constants.AUTH_PATH + Constants.AUTH_LOGOUT_PATH,
        null,
        {
            headers: {
                Authorization: `${Constants.BEARER_PREFIX} ${sessionAccessToken}`,
            },
        }
    ).then(() => {
        apolloClient.clearStore();
        removeSessionAccessToken();
        disableLoggers();
        dispatch({
            type: USER_LOGOUT,
        });
        dispatch(showInfoNotification(message));
    }).catch(() => {
        dispatch(showErrorNotification(errorMessage));
    });
};

export const LOAD_ACCESS_TOKEN = 'LOAD_ACCESS_TOKEN';
export const loadAccessToken = () => (dispatch) => {
    const accessToken = getSessionAccessToken();
    if (accessToken) {
        dispatch(setDaysToPasswordExpiration(accessToken));
    }
    dispatch({
        type: LOAD_ACCESS_TOKEN,
        payload: accessToken,
    });
};

export const refreshToken = (userId, oldAccessToken) => (dispatch) => (
    axios.post(Constants.AUTH_PATH + Constants.AUTH_REFRESH_TOKEN_PATH)
        .then(onRefreshTokenResponse(dispatch, userId, oldAccessToken))
        .catch(onRefreshTokenError(dispatch, userId, oldAccessToken))
);

export const onRefreshTokenResponse = (dispatch, userId, oldAccessToken) => (response) => {
    const newAccessToken = extractAccessToken(response);
    if (newAccessToken) {
        dispatch(refreshTokenSuccess(newAccessToken));
        dispatch(setDaysToPasswordExpiration(newAccessToken));
    } else {
        dispatch(tokenRefreshFailed(oldAccessToken));
        logger.warn(`Access Token for user '${userId}' could not be refreshed due to missing authorization token in the response: ${JSON.stringify(response)}`);
    }
};

export const onRefreshTokenError = (dispatch, userId, oldAccessToken) => (error) => {
    const { response } = error;
    if (response) {
        if (response.status === 401) {
            dispatch(unauthorizedTokenRefresh());
            const message = getI18n().t('token-refresh.unauthorized');
            dispatch(showErrorNotification(message));
            logger.warn(message);
        } else {
            dispatch(tokenRefreshFailed(oldAccessToken));
            logger.warn(`Access Token for user '${userId}' could not be refreshed: ${response.statusText}`);
        }
    } else {
        dispatch(tokenRefreshFailed(oldAccessToken));
        logger.warn(`Access Token for user '${userId}' could not be refreshed: ${error.message}`);
    }
};

export const TOKEN_REFRESH_FAILURE = 'TOKEN_REFRESH_FAILURE';
export const tokenRefreshFailed = (accessToken) => ({
    type: TOKEN_REFRESH_FAILURE,
    payload: accessToken,
});

export const UNAUTHORIZED_TOKEN_REFRESH = 'UNAUTHORIZED_TOKEN_REFRESH';
export const unauthorizedTokenRefresh = () => ({
    type: UNAUTHORIZED_TOKEN_REFRESH,
});

export const REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS';
export const refreshTokenSuccess = (accessToken) => {
    setSessionAccessToken(accessToken);
    return {
        type: REFRESH_TOKEN_SUCCESS,
        payload: accessToken,
    };
};

export const UNAUTHORIZED_ACCESS = 'UNAUTHORIZED_ACCESS';
export const unauthorizedAccess = (message, apolloClient) => (dispatch) => {
    apolloClient.resetStore();
    dispatch({
        type: UNAUTHORIZED_ACCESS,
    });
    dispatch(showErrorNotification(message));
};

export const REMOVE_OLD_TOKEN = 'REMOVE_OLD_TOKEN';
export const removeOldToken = () => (dispatch) => {
    dispatch(showInfoNotification(getI18n().t('logout.token-invalid')));
    dispatch({
        type: REMOVE_OLD_TOKEN,
    });
};
